import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Loader2, RotateCcw, Plus, Trash2, GripVertical,
  Home as HomeIcon, Video, Columns, ListOrdered, MessageSquareQuote
} from 'lucide-react';
import { adminFetch } from '@/utils/adminAuth';
import { invalidateHomepageConfig } from '@/hooks/useHomepageConfig';

const TABS = [
  { id: 'hero', label: 'Hero', Icon: HomeIcon },
  { id: 'featuredVideo', label: 'Featured Video', Icon: Video },
  { id: 'threeColumns', label: '3-Column Services', Icon: Columns },
  { id: 'whyChooseUs', label: 'Why Choose Us', Icon: ListOrdered },
  { id: 'testimonials', label: 'Testimonials', Icon: MessageSquareQuote },
];

const Field = ({ label, value, onChange, type = 'text', placeholder = '', textarea = false, rows = 3, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
        data-testid={testid}
      />
    )}
  </label>
);

export const AdminHomepage = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('hero');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-config`);
      const data = await res.json();
      setCfg(data);
    } catch (e) {
      toast.error('Failed to load homepage config');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (partial) => {
    setSaving(true);
    try {
      const res = await adminFetch('/admin/homepage-config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partial),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const data = await res.json();
      setCfg(data);
      invalidateHomepageConfig();
      toast.success('Saved. Refresh the homepage to see updates.');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset ALL homepage content to defaults? This deletes your edits.')) return;
    try {
      const res = await adminFetch('/admin/homepage-config/reset', { method: 'POST' });
      if (!res.ok) throw new Error('Reset failed');
      invalidateHomepageConfig();
      toast.success('Reset complete');
      await load();
    } catch (e) { toast.error(e.message || 'Reset failed'); }
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-5xl mx-auto py-10 px-6">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-muted-foreground hover:text-primary" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-black">Homepage Editor</h1>
              <p className="text-sm text-muted-foreground">Edit the copy, videos and testimonials shown on your public homepage.</p>
            </div>
          </div>
          <button
            onClick={resetAll}
            className="px-3 py-2 rounded-lg bg-card border border-border hover:border-yellow-500/60 text-sm font-semibold flex items-center gap-2 text-yellow-400"
            data-testid="reset-homepage"
          >
            <RotateCcw className="w-4 h-4" />
            Reset all to defaults
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap flex items-center gap-2 border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border/50 hover:text-primary hover:border-primary/50'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          {tab === 'hero' && <HeroTab cfg={cfg} setCfg={setCfg} />}
          {tab === 'featuredVideo' && <FeaturedVideoTab cfg={cfg} setCfg={setCfg} />}
          {tab === 'threeColumns' && <ThreeColumnsTab cfg={cfg} setCfg={setCfg} />}
          {tab === 'whyChooseUs' && <WhyChooseUsTab cfg={cfg} setCfg={setCfg} />}
          {tab === 'testimonials' && <TestimonialsTab cfg={cfg} setCfg={setCfg} />}

          <div className="pt-4 border-t border-border flex items-center justify-end">
            <button
              onClick={() => save({ [tab]: cfg[tab] })}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="save-tab-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save {TABS.find(t => t.id === tab)?.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroTab = ({ cfg, setCfg }) => {
  const h = cfg.hero || {};
  const set = (k, v) => setCfg({ ...cfg, hero: { ...h, [k]: v } });
  return (
    <div className="space-y-4">
      <Field label="Badge (top pill text)" value={h.badge} onChange={v => set('badge', v)} testid="hero-badge" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title (top line)" value={h.titleTop} onChange={v => set('titleTop', v)} testid="hero-title-top" />
        <Field label="Title (bottom line, gold)" value={h.titleBottom} onChange={v => set('titleBottom', v)} testid="hero-title-bottom" />
      </div>
      <Field label="Quote line under title" value={h.quote} onChange={v => set('quote', v)} textarea rows={2} testid="hero-quote" />
      <Field label="Banner strip under buttons" value={h.ctaBanner} onChange={v => set('ctaBanner', v)} textarea rows={2} testid="hero-banner" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Button 1 label" value={h.btn1Label} onChange={v => set('btn1Label', v)} testid="hero-btn1-label" />
        <Field label="Button 1 link" value={h.btn1Href} onChange={v => set('btn1Href', v)} testid="hero-btn1-href" />
        <Field label="Button 2 label" value={h.btn2Label} onChange={v => set('btn2Label', v)} testid="hero-btn2-label" />
        <Field label="Button 2 link" value={h.btn2Href} onChange={v => set('btn2Href', v)} testid="hero-btn2-href" />
        <Field label="Button 3 label" value={h.btn3Label} onChange={v => set('btn3Label', v)} testid="hero-btn3-label" />
        <Field label="Button 3 link" value={h.btn3Href} onChange={v => set('btn3Href', v)} testid="hero-btn3-href" />
      </div>
      <p className="text-xs text-muted-foreground">
        Tip: for hero slideshow photos, use the separate <Link to="/admin/hero-slides" className="text-primary underline">Hero Slides Editor</Link>.
      </p>
    </div>
  );
};

const FeaturedVideoTab = ({ cfg, setCfg }) => {
  const v = cfg.featuredVideo || {};
  const set = (k, val) => setCfg({ ...cfg, featuredVideo: { ...v, [k]: val } });
  return (
    <div className="space-y-4">
      <Field
        label="Vimeo Video ID"
        value={v.vimeoId}
        onChange={val => set('vimeoId', val.replace(/[^0-9]/g, ''))}
        placeholder="e.g. 112214453"
        testid="video-id"
      />
      <Field label="Title (shown under the play button)" value={v.title} onChange={val => set('title', val)} testid="video-title" />
      <Field label="Poster image URL" value={v.poster} onChange={val => set('poster', val)} testid="video-poster" />
      {v.poster && (
        <div className="aspect-video rounded-lg overflow-hidden bg-black/40 max-w-md">
          <img loading="lazy" decoding="async" src={v.poster} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
};

const ThreeColumnsTab = ({ cfg, setCfg }) => {
  const cols = cfg.threeColumns || [];
  const setCol = (idx, k, val) => {
    const next = cols.map((c, i) => i === idx ? { ...c, [k]: val } : c);
    setCfg({ ...cfg, threeColumns: next });
  };
  return (
    <div className="space-y-6">
      {cols.map((col, idx) => (
        <div key={idx} className="p-4 rounded-lg bg-background border border-border/60 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-primary">Column {idx + 1}</div>
          <Field label="Title" value={col.title} onChange={v => setCol(idx, 'title', v)} testid={`col-${idx}-title`} />
          <Field label="Body" value={col.body} onChange={v => setCol(idx, 'body', v)} textarea rows={4} testid={`col-${idx}-body`} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Link label" value={col.linkLabel} onChange={v => setCol(idx, 'linkLabel', v)} testid={`col-${idx}-label`} />
            <Field label="Link target (path)" value={col.to} onChange={v => setCol(idx, 'to', v)} placeholder="/performances" testid={`col-${idx}-to`} />
          </div>
        </div>
      ))}
    </div>
  );
};

const WhyChooseUsTab = ({ cfg, setCfg }) => {
  const w = cfg.whyChooseUs || {};
  const setW = (k, v) => setCfg({ ...cfg, whyChooseUs: { ...w, [k]: v } });
  const reasons = Array.isArray(w.reasons) ? w.reasons : [];
  const setReason = (idx, v) => {
    const next = reasons.map((r, i) => i === idx ? v : r);
    setW('reasons', next);
  };
  const addReason = () => setW('reasons', [...reasons, '']);
  const removeReason = (idx) => setW('reasons', reasons.filter((_, i) => i !== idx));
  return (
    <div className="space-y-4">
      <Field label="Heading" value={w.heading} onChange={v => setW('heading', v)} testid="wc-heading" />
      <Field label="Bottom tagline" value={w.tagline} onChange={v => setW('tagline', v)} textarea rows={2} testid="wc-tagline" />
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Numbered Reasons</span>
          <button onClick={addReason} className="text-xs font-bold text-primary hover:underline flex items-center gap-1" data-testid="add-reason">
            <Plus className="w-3 h-3" /> Add reason
          </button>
        </div>
        <div className="space-y-2">
          {reasons.map((r, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <span className="mt-2 text-primary font-bold w-6 text-right">{idx + 1}.</span>
              <textarea
                value={r}
                onChange={e => setReason(idx, e.target.value)}
                rows={3}
                className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                data-testid={`reason-${idx}`}
              />
              <button
                onClick={() => removeReason(idx)}
                className="p-2 rounded-lg hover:bg-red-500/15 text-red-400"
                title="Remove"
                data-testid={`remove-reason-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TestimonialsTab = ({ cfg, setCfg }) => {
  const items = Array.isArray(cfg.testimonials) ? cfg.testimonials : [];
  const setItem = (idx, k, v) => {
    const next = items.map((it, i) => i === idx ? { ...it, [k]: v } : it);
    setCfg({ ...cfg, testimonials: next });
  };
  const add = () => setCfg({ ...cfg, testimonials: [...items, { name: '', role: '', quote: '' }] });
  const remove = (idx) => setCfg({ ...cfg, testimonials: items.filter((_, i) => i !== idx) });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Testimonials</span>
        <button onClick={add} className="text-xs font-bold text-primary hover:underline flex items-center gap-1" data-testid="add-testimonial">
          <Plus className="w-3 h-3" /> Add testimonial
        </button>
      </div>
      <div className="space-y-4">
        {items.map((t, idx) => (
          <div key={idx} className="p-4 rounded-lg bg-background border border-border/60 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-primary">#{idx + 1}</span>
              <button
                onClick={() => remove(idx)}
                className="p-2 rounded-lg hover:bg-red-500/15 text-red-400"
                title="Remove"
                data-testid={`remove-testimonial-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" value={t.name} onChange={v => setItem(idx, 'name', v)} testid={`t-name-${idx}`} />
              <Field label="Role / Organization" value={t.role} onChange={v => setItem(idx, 'role', v)} testid={`t-role-${idx}`} />
            </div>
            <Field label="Quote" value={t.quote} onChange={v => setItem(idx, 'quote', v)} textarea rows={4} testid={`t-quote-${idx}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminHomepage;
